import { Button } from '@amore/ui/components/button'
import { Input } from '@amore/ui/components/input'
import { useNavigate, useParams } from '@tanstack/react-router'
import { CalendarPlus, ChevronRight, Search } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { formatDate } from '@/lib/format'
import { useWedding } from '@/mock/store'
import type { Party, Person, WebReply } from '@/mock/types'
import { fold } from '@/screens/guests/bulk'
import { partyTitle } from '@/screens/guests/model'
import { useToast } from '@/shell/toaster'
import { ReplyForm } from './reply-form'
import { ThankYou } from './thank-you'

/**
 * The guest website, opened from the one shared link. First step of replying:
 * "Kdo jste?" — the guest types their name and picks their invitation. Nothing
 * is listed until they type (the guest list stays private), and only people
 * already on the list can be found.
 */
export function GuestSiteScreen() {
  const data = useWedding()
  const { showToast } = useToast()
  const { brideName, groomName, date } = data.identity
  const couple = brideName && groomName ? `${brideName} & ${groomName}` : 'Naše svatba'
  const venue = data.suppliers.find((supplier) => supplier.category === 'venue')
  const { published, rsvpEnabled, rsvpDeadline } = data.website
  const navigate = useNavigate()
  const { partyId } = useParams({ strict: false }) as { partyId?: string }
  const [sent, setSent] = useState<WebReply | null>(null)
  const party = data.parties.find((item) => item.id === partyId)

  if (!published)
    return (
      <Shell>
        <div className="space-y-3 py-24 text-center">
          <h1 className="font-heading text-3xl">Web zatím není zveřejněný</h1>
          <p className="text-muted-foreground">
            Novomanželé ho ještě připravují. Zkuste odkaz otevřít později.
          </p>
        </div>
      </Shell>
    )

  if (party && rsvpEnabled)
    return (
      <Shell>
        {sent ? (
          <ThankYou
            party={party}
            reply={sent}
            onHome={() => {
              setSent(null)
              void navigate({ to: '/svatba' })
            }}
          />
        ) : (
          <ReplyForm
            party={party}
            onBack={() => void navigate({ to: '/svatba' })}
            onSent={setSent}
          />
        )}
      </Shell>
    )

  return (
    <Shell>
      <header className="space-y-5 px-2 pt-14 pb-12 text-center sm:pt-20">
        <p className="text-xs font-semibold tracking-[0.24em] text-action uppercase">
          Budeme se brát
        </p>
        <h1 className="font-heading text-5xl leading-none tracking-tight sm:text-6xl">{couple}</h1>
        <p className="text-lg">
          {date ? formatDate(date, 'long') : 'Datum doplníme'}
          {venue ? <span className="text-muted-foreground"> · {venue.name}</span> : null}
        </p>
        {date ? (
          <Button
            appearance="outline"
            tone="neutral"
            size="sm"
            onClick={() =>
              showToast({
                title: 'Uložili bychom do kalendáře',
                description: 'V prototypu jen na oko.',
              })
            }
          >
            <CalendarPlus aria-hidden="true" className="size-3.5" />
            Přidat do kalendáře
          </Button>
        ) : null}
      </header>

      <section
        aria-labelledby="rsvp"
        className="space-y-6 rounded-[1.75rem] border border-border bg-card p-6 shadow-sm sm:p-8"
      >
        <div className="space-y-1.5 text-center">
          <h2 id="rsvp" className="font-heading text-3xl">
            Přijdete?
          </h2>
          <p className="text-muted-foreground">
            {rsvpEnabled
              ? rsvpDeadline
                ? `Dejte nám prosím vědět do ${formatDate(rsvpDeadline, 'long')}.`
                : 'Dejte nám prosím vědět, jestli přijdete.'
              : 'Odpovědi už nepřijímáme. Pokud se něco změnilo, napište nám prosím.'}
          </p>
        </div>
        {rsvpEnabled ? <WhoAreYou parties={data.parties} /> : null}
      </section>

      <p className="py-10 text-center text-xs text-muted-foreground">Vytvořeno ve Slibby</p>
    </Shell>
  )
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh bg-background">
      <meta name="robots" content="noindex,nofollow" />
      <main className="mx-auto max-w-xl px-4">{children}</main>
    </div>
  )
}

/** "nov" finds Nováková and "jana nov" finds Jana Nováková: a name starts with what was typed. */
const nameMatches = (person: Person, needle: string) => {
  const name = fold(`${person.firstName} ${person.lastName}`)
  return name.startsWith(needle) || name.split(' ').some((word) => word.startsWith(needle))
}

/** Minimum letters before anything is shown, so the list can't be browsed. */
const minLetters = 3

/**
 * "Kdo jste?": type a first name or surname, pick your invitation. One pick
 * covers the whole invitation (a couple, a family); B2 is the reply form.
 */
function WhoAreYou({ parties }: { parties: Party[] }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const needle = fold(query)
  const results =
    needle.length < minLetters
      ? []
      : parties.filter((party) => party.persons.some((person) => nameMatches(person, needle)))

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="who" className="block text-center text-sm font-semibold">
          Kdo jste?
        </label>
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id="who"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Vaše jméno nebo příjmení"
            autoComplete="off"
            className="h-12 rounded-full pl-11 text-base"
          />
        </div>
      </div>

      {needle.length < minLetters ? (
        <p className="text-center text-sm text-muted-foreground">
          Najděte se v seznamu a odpovězte za celou svou pozvánku.
        </p>
      ) : results.length === 0 ? (
        <div className="space-y-1 text-center text-sm">
          <p className="font-semibold">Nenašli jsme vás</p>
          <p className="text-muted-foreground">
            Zkuste jen příjmení nebo jméno bez diakritiky. Když to nepůjde, napište prosím
            novomanželům.
          </p>
        </div>
      ) : (
        <ul
          aria-label="Nalezené pozvánky"
          className="divide-y divide-border rounded-2xl border border-border"
        >
          {results.slice(0, 5).map((party) => (
            <li key={party.id}>
              <button
                type="button"
                onClick={() => void navigate({ to: `/svatba/${party.id}` })}
                className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors first:rounded-t-2xl last:rounded-b-2xl hover:bg-muted/50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
              >
                <span className="min-w-0 flex-1 font-semibold">{partyTitle(party)}</span>
                <ChevronRight
                  aria-hidden="true"
                  className="size-4 shrink-0 text-muted-foreground"
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
